// ─────────────────────────────────────────────
//  GoLivePage — "set up my organization for real".
//
//  ONE form, TWO entry points, and the difference matters:
//
//   • From /get-started, the router state carries { email, password } and NO
//     account exists yet. The account is created HERE (signUp) before the
//     request is filed, so the request can be attributed to a real uid — the
//     security rule is self-attributed and would reject anything else. The
//     sandbox flag is deliberately NOT set on this path: this person asked for
//     a real organization, not a test one.
//
//   • From inside the sandbox, the person is already signed in and has already
//     answered the wizard, so the fields pre-fill from their profile and the
//     whole answer set rides along as wizardAnswers. Their sandbox is untouched
//     — nothing here writes to the local store, and the confirmation says so,
//     because the obvious fear at this moment is losing the board they built.
//
//  The request itself goes through src/sandbox/orgRequest, the one module that
//  deliberately bypasses the sandbox db swap.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signUp } from "../firebase/auth";
import { submitOrgRequest } from "../sandbox/orgRequest";
import { useAuth } from "../context/AuthContext";
import { FACILITY_TYPES, validateStep } from "../domain/onboarding";
import { Card, Field, Button } from "../components";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

const NO_ERRORS = { orgName: null, facilityType: null, orCount: null, contactEmail: null };

// Deliberately permissive: this is a "can we reach you" check, not an RFC 5322
// parser. Anything stricter rejects real addresses and buys nothing.
const emailError = (value) =>
  typeof value === "string" && /.+@.+\..+/.test(value.trim())
    ? null
    : "Enter a contact email so we can reach you.";

export default function GoLivePage() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const { state } = useLocation();

  // Credentials handed over by /get-started. Their presence is what says "no
  // account exists yet"; AuthProvider has already resolved by the time any
  // child renders, so `user` is settled and safe to read as an initial value.
  const pendingEmail = state?.email || "";
  const pendingPassword = state?.password || "";
  const fromSignup = !!(pendingEmail && pendingPassword);
  const fromSandbox = user?.sandbox === true;

  const onboarding = fromSandbox && user?.onboarding ? user.onboarding : null;

  const [orgName, setOrgName] = useState(onboarding?.orgName || "");
  const [facilityType, setFacilityType] = useState(
    FACILITY_TYPES.includes(onboarding?.facilityType) ? onboarding.facilityType : ""
  );
  // Held as a STRING. It is parsed with parseInt at submit time and NaN is
  // passed straight to the validator — never Number(), which turns "" and null
  // into a valid-looking 0.
  const [orCountRaw, setOrCountRaw] = useState(
    Number.isFinite(onboarding?.orCount) ? String(onboarding.orCount) : ""
  );
  const [contactEmail, setContactEmail] = useState(pendingEmail || user?.email || "");

  const [errors, setErrors] = useState(NO_ERRORS);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    const orCount = Number.parseInt(orCountRaw, 10);
    const next = {
      orgName: validateStep("org", orgName),
      facilityType: validateStep("facilityType", facilityType),
      orCount: validateStep("rooms", orCount),
      contactEmail: emailError(contactEmail),
    };
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSubmitting(true);
    try {
      // Create the account FIRST on the signup path — the rule requires the
      // request to carry the caller's own uid, so there is nothing to file
      // until an identity exists.
      let uid = user?.uid || null;
      if (fromSignup) {
        const account = await signUp(pendingEmail, pendingPassword);
        uid = account?.uid || uid;
      }

      await submitOrgRequest({
        uid,
        orgName: orgName.trim(),
        facilityType,
        orCount,
        contactEmail: contactEmail.trim(),
        source: fromSandbox ? "sandbox-upgrade" : "signup",
        // The later migration needs the wizard answers and they cannot be
        // reconstructed after the fact; there are none on the signup path.
        wizardAnswers: onboarding || null,
      });
      setDone(true);
    } catch (err) {
      console.error("Organization request failed:", err);
      setFormError(
        "We could not send your request. Check your connection and try again — " +
          "nothing was lost."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <Card variant="elevated">
            <h1 style={styles.heading}>Request sent</h1>
            <div style={styles.confirm} role="status">
              <p style={styles.paragraph}>
                Thanks — we have your request. Setting up a shared workspace is
                not automatic yet, so someone will get in touch to finish it.
              </p>
              {fromSandbox ? (
                <p style={styles.paragraph}>
                  Your test setup on this phone is untouched — keep using it.
                </p>
              ) : null}
            </div>
            {fromSandbox ? (
              <Button variant="secondary" size="full" onClick={() => navigate("/board")}>
                Back to my board
              </Button>
            ) : null}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <Card variant="elevated">
          <h1 style={styles.heading}>Set up your organization</h1>
          <p style={styles.intro}>
            Tell us about your facility and we will get a shared workspace ready
            for your team.
            {fromSandbox
              ? " Your test setup on this phone keeps working either way."
              : ""}
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <Field
              id="golive-org"
              label="Organization name"
              placeholder="Mercy General"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              error={errors.orgName}
              required
            />

            <fieldset style={styles.fieldset}>
              <legend style={styles.legend}>What kind of facility is it?</legend>
              {FACILITY_TYPES.map((type) => {
                const id = "golive-type-" + type.replace(/\W+/g, "-").toLowerCase();
                return (
                  <div key={type} style={styles.radioRow}>
                    <input
                      id={id}
                      type="radio"
                      name="golive-facility-type"
                      value={type}
                      checked={facilityType === type}
                      onChange={() => setFacilityType(type)}
                      style={styles.radio}
                    />
                    <label htmlFor={id} style={styles.radioLabel}>{type}</label>
                  </div>
                );
              })}
              {errors.facilityType ? (
                <div role="alert" style={styles.error}>{errors.facilityType}</div>
              ) : null}
            </fieldset>

            <Field
              id="golive-ors"
              label="Operating rooms"
              type="number"
              placeholder="6"
              value={orCountRaw}
              onChange={(e) => setOrCountRaw(e.target.value)}
              error={errors.orCount}
              required
            />

            <Field
              id="golive-email"
              label="Contact email"
              type="email"
              placeholder="you@hospital.com"
              helperText="Where we should reach you about setting this up."
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              error={errors.contactEmail}
              required
            />

            {formError ? (
              <div role="alert" style={styles.error}>{formError}</div>
            ) : null}

            <Button variant="primary" size="full" type="submit" loading={submitting}>
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: color.surface.base,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.md,
  },
  shell: { width: 440, maxWidth: "95vw" },
  heading: {
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    fontFamily: font.family,
    lineHeight: font.lineHeight.tight,
    margin: 0,
  },
  intro: {
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
    fontFamily: font.family,
    margin: `${space.sm}px 0 ${space.lg}px`,
  },
  paragraph: {
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.primary,
    fontFamily: font.family,
    margin: `0 0 ${space.sm}px`,
  },
  confirm: {
    background: color.surface.raised,
    border: "1px solid " + color.border.default,
    borderRadius: radius.md,
    padding: space.md,
    margin: `${space.md}px 0`,
  },
  fieldset: {
    border: "1px solid " + color.border.default,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    minWidth: 0, // fieldsets refuse to shrink below their content without this
  },
  legend: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    fontFamily: font.family,
    padding: `0 ${space.xs}px`,
  },
  // 44 px minimum touch target (WCAG 2.2 AA) on the whole row, so the label is
  // as tappable as the control.
  radioRow: { display: "flex", alignItems: "center", gap: space.sm, minHeight: 44 },
  radio: { width: 20, height: 20, accentColor: color.accent.primary, flexShrink: 0 },
  radioLabel: {
    flex: 1,
    fontSize: font.size.body,
    color: color.text.primary,
    fontFamily: font.family,
    lineHeight: font.lineHeight.normal,
    cursor: "pointer",
  },
  error: {
    background: color.surface.error,
    border: "1px solid " + color.accent.error,
    // Ink, not accent.error: #dc2626 on this ground is 4.41:1 and FAILS AA for
    // normal text (tokens.js used to assert 4.95:1 and pass — it was wrong, and
    // four screens had been consolidated onto that sentence). accent.error stays
    // correct as the BORDER above; it is only as text that it falls short.
    color: color.surface.errorInk,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginBottom: space.sm,
    fontFamily: font.family,
  },
};
